import React, { forwardRef, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { GradientColorHeader, ProductInfo, ReadTextField } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import {
	Box,
	Button,
	IconButton,
	MenuItem,
	SelectField,
	Stack,
	TextField,
	Typography,
	Icons,
	DateTimeStandard
} from '@curves/ui-base';
import { getNowTime, parseDate } from "@util/moment";
import { callRef, ensureArray, isEqual, isFunction, refIsRequiredError, toNumber } from "@util/utils";
import { CLUB_SOURCE_TYPE } from "@clubInventory/constants/dataEnums";
import useOptions from "@util/hook/useOptions";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import useClubPurchaseReceiptApi from "@apis/useClubPurchaseReceiptApi";
import useProductApi from "@apis/useProductApi";
import { SkuAddToList, InventoryProductList, InputGroup } from "@clubInventory/components/";
import { usePurchaseSkuAdd } from "@clubInventory/hooks/useSkuAddToList";

// 功能：進貨商品清單欄位定義
const purchaseHeaderRow = [
	{
		cellKey: "productName",
		headerLabel: "商品清單",
		minWidth: 200,
		formatCell: (productName, row) => (
			<ProductInfo
				product={{
					name: productName,
					sku: row.sku,
					spec: row.variantName,
					imageUrl: row.pictureSrc,
				}}
			/>
		),
	},
	{
		cellKey: "expectedQuantity",
		headerLabel: "數量",
		align: "center",
		minWidth: 120,
		formatCell: (expectedQuantity, row, { onOpenQuantityEdit }) => (
			<Typography
				className={onOpenQuantityEdit ? "mb-0 editable-quantity" : "mb-0"}
				style={{ cursor: onOpenQuantityEdit ? "pointer" : "default" }}
				onClick={(e) => {
					e.stopPropagation();
					isFunction(onOpenQuantityEdit) && onOpenQuantityEdit(row);
				}}
			>
				{expectedQuantity}
			</Typography>
		),
	},
	{
		cellKey: "actions",
		headerLabel: "",
		align: "center",
		minWidth: 80,
		formatCell: (_, row, { onDelete, getProductKey }) => {
			const productKey = getProductKey?.(row) ?? `${row.productID}_${row.variantID ?? ""}`;
			return (
				<IconButton size="small" onClick={() => onDelete?.(productKey)}>
					<Icons.Trash />
				</IconButton>
			);
		},
	},
];

// 功能：送出 API payload 的 item 映射
const toPayloadItem = (p) => ({
	productID: p.productID,
	variantID: p.variantID || 0,
	quantity: Number(p.expectedQuantity) || 0,
});

// 功能：新增進貨單（掃碼加入商品、填寫貨源/分店、送出建立）
const CreatePurchaseDialog = forwardRef(({ open, onClose, refresh, ...props }, ref) => {
	// 功能：表單欄位 ref（驗收人、貨源、分店、備註）
	const inspectorRef = useRef(null);
	const sourceTypeRef = useRef(null);
	const branchRef = useRef(null);
	const noteRef = useRef(null);

	const { getClubPurchaseReceiptOptionsApi, postClubPurchaseReceiptApi } = useClubPurchaseReceiptApi();
	const { getProductBySkuApi } = useProductApi();
	const { options, getOptionsApi } = useOptions({
		getOptionsApi: getClubPurchaseReceiptOptionsApi,
		isFetchOnMount: false,
	});
	const { employeeOption } = useGetEmployeeOptionState();

	// 功能：目前選的貨源，用於決定是否顯示分店欄位
	const [sourceType, setSourceType] = useState(null);

	// 功能：掃碼/輸入 SKU 加入商品，fetchProductBySku 依 SKU 查商品後回傳列表用格式
	const skuAdd = usePurchaseSkuAdd({
		fetchProductBySku: async (sku) => {
			const match = await getProductBySkuApi({ text: sku });
			if (!match) return null;
			return {
				productID: match.productID,
				productName: match.productName,
				variantID: match.variantID,
				variantName: match.variantName,
				sku: match.sku,
				pictureSrc: match.pictureSrc,
				actualQuantity: 1,
			};
		},
	});
	const { products: productList, clearProducts: clearProductList } = skuAdd;

	// 功能：Dialog 開啟時載入貨源/分店選項
	useEffect(() => {
		if (open && isFunction(getOptionsApi)) getOptionsApi(true);
		// eslint-disable-next-line
	}, [open]);

	// 功能：關閉時清空商品列表並呼叫 onClose
	const handleClose = (e) => {
		clearProductList();
		isFunction(onClose) && onClose(e);
	};

	// 功能：貨源變更時更新 sourceType（控制分店欄位顯示）
	const handleSourceTypeChange = (event, node, value) => setSourceType(toNumber(value));

	// 功能：驗證必填與商品非空 → 依貨源決定 clubID → 組 payload 送出，成功則刷新並關閉
	const handleSubmit = async () => {
		const requiredRefs = [inspectorRef, sourceTypeRef];
		if (isEqual(sourceType, CLUB_SOURCE_TYPE.CLUBTRANSFER)) requiredRefs.push(branchRef);
		if (refIsRequiredError(...requiredRefs)) return;
		if (productList.length === 0) return;

		const SourceClubID = isEqual(sourceType, CLUB_SOURCE_TYPE.CLUBTRANSFER)
			? callRef(branchRef, "getResult")
			: undefined;
		if (isEqual(sourceType, CLUB_SOURCE_TYPE.CLUBTRANSFER) && !SourceClubID) return;

		const payload = {
			purchaseDate: getNowTime(),
			employeeID: callRef(inspectorRef, "getResult"),
			sourceType: callRef(sourceTypeRef, "getResult"),
			SourceClubID,
			items: productList.map(toPayloadItem),
			notes: callRef(noteRef, "getResult") || undefined,
		};

		const resp = await postClubPurchaseReceiptApi(payload);
		if (resp) {
			isFunction(refresh) && refresh();
			handleClose();
		}
	};

	return (
		<Dialog
			className="simple-content dialog"
			PaperProps={{ className: "wx-90rem" }}
			open={open}
			onClose={handleClose}
			fullWidth
			{...props}
		>
			<GradientColorHeader onClose={handleClose}>新增進貨單</GradientColorHeader>
			<DialogContent className="p-3">
				<Grid container spacing={3}>
					<Grid xs={4} item>
						<Stack spacing={2} direction="column">
							<ReadTextField label="建單日期">
								{parseDate(getNowTime(), DateTimeStandard.DisplayDate)}
							</ReadTextField>
							<EmployeeSelect label="驗收人" ref={inspectorRef} options={employeeOption} fullWidth required />
							<SelectField
								label="貨源"
								ref={sourceTypeRef}
								onChange={handleSourceTypeChange}
								fullWidth
								required
							>
								{ensureArray(options?.clubSourceTypeOptions).map(({ text, value }) => (
									<MenuItem key={value} value={value}>
										{text}
									</MenuItem>
								))}
							</SelectField>
							{isEqual(sourceType, CLUB_SOURCE_TYPE.CLUBTRANSFER) && (
								<SelectField label="分店" ref={branchRef} fullWidth required>
									{ensureArray(options?.clubOptions).map(({ text, value }) => (
										<MenuItem key={value} value={value}>
											{text}
										</MenuItem>
									))}
								</SelectField>
							)}
							<TextField ref={noteRef} label="備註" rows={2} multiline fullWidth />
						</Stack>
					</Grid>
					<Grid xs={8} item>
						<SkuAddToList {...skuAdd}>
							<Stack spacing={2} alignItems="center" className="mb-3">
								<InputGroup className="product-input-group">
									<InputGroup.StartAdornment>
										<SkuAddToList.ScanButton />
									</InputGroup.StartAdornment>
									<InputGroup.Input>
										<SkuAddToList.Input placeholder="請使用手持掃描器掃描或輸入商品SKU" />
									</InputGroup.Input>
								</InputGroup>
								<SkuAddToList.SubmitButton>加入商品</SkuAddToList.SubmitButton>
							</Stack>
							<SkuAddToList.ScannerDialog title="商品掃描" continuousScan />
							<SkuAddToList.ConfirmDialog />
							<SkuAddToList.List>
								<InventoryProductList
									headerRow={purchaseHeaderRow}
									emptyMessage="請掃描商品條碼"
									height="406px"
								/>
							</SkuAddToList.List>
						</SkuAddToList>
					</Grid>
				</Grid>
				<Box className="mt-3 text-right">
					<Button variant="contained" onClick={handleSubmit}>
						確認新增
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
});

CreatePurchaseDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	refresh: PropTypes.func,
};

CreatePurchaseDialog.displayName = "CreatePurchaseDialog";

export default CreatePurchaseDialog;
